import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Phone, PhoneCall, PhoneMissed, PhoneOff, Clock, TrendingUp, TrendingDown,
  Download, Loader2, Plus, Trophy, Users, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

// ----------------------------- helpers -----------------------------
const fmtDuration = (secs) => {
  const s = Number(secs) || 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return r ? `${m}m ${r}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch (_e) { return iso; }
};
const STATUS_CONFIG = {
  CONNECTED: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: PhoneCall, label: 'Connected' },
  MISSED:    { color: 'bg-amber-100 text-amber-700 border-amber-200',       icon: PhoneMissed, label: 'Missed' },
  REJECTED:  { color: 'bg-rose-100 text-rose-700 border-rose-200',          icon: PhoneOff,   label: 'Rejected' },
  BUSY:      { color: 'bg-orange-100 text-orange-700 border-orange-200',    icon: Phone,      label: 'Busy' },
  NO_ANSWER: { color: 'bg-slate-100 text-slate-700 border-slate-200',       icon: PhoneOff,   label: 'No Answer' },
  FAILED:    { color: 'bg-rose-100 text-rose-700 border-rose-200',          icon: PhoneOff,   label: 'Failed' },
};
const StatusBadge = ({ status }) => {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.FAILED;
  const Icon = c.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${c.color}`} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3" /> {c.label}
    </Badge>
  );
};

const Stat = ({ label, value, tone = 'default', icon: Icon, testId }) => {
  const tones = {
    default: 'bg-card',
    success: 'bg-emerald-50 border-emerald-200',
    warn:    'bg-amber-50 border-amber-200',
    danger:  'bg-rose-50 border-rose-200',
    info:    'bg-sky-50 border-sky-200',
  };
  return (
    <div className={`border rounded-lg p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <p className="text-3xl font-semibold mt-1">{value ?? '—'}</p>
    </div>
  );
};

// ----------------------------- Log Call Dialog -----------------------------
export const LogCallDialog = ({ open, onOpenChange, leadId, leadName, defaultMobile, onLogged }) => {
  const { api } = useAuth();
  const [form, setForm] = useState({
    mobileNumber: defaultMobile || '',
    callStatus: 'CONNECTED',
    durationSeconds: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        mobileNumber: defaultMobile || '',
        callStatus: 'CONNECTED',
        durationSeconds: '',
        notes: '',
      });
    }
  }, [open, defaultMobile]);

  const submit = async () => {
    if (!form.mobileNumber || form.mobileNumber.replace(/\D/g, '').length < 10) {
      return toast.error('Enter a valid 10-digit mobile number');
    }
    setSaving(true);
    try {
      const now = new Date();
      const dur = parseInt(form.durationSeconds || '0', 10);
      const start = new Date(now.getTime() - dur * 1000).toISOString();
      const payload = {
        leadId: leadId || null,
        mobileNumber: form.mobileNumber,
        callStatus: form.callStatus,
        direction: 'OUTBOUND',
        durationSeconds: dur,
        source: 'manual',
        startTime: start,
        endTime: now.toISOString(),
        notes: form.notes || null,
      };
      const { data } = await api.post('/call-tracking', payload);
      toast.success('Call logged');
      onOpenChange(false);
      onLogged && onLogged(data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to log call';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="log-call-dialog">
        <DialogHeader>
          <DialogTitle>Log a call{leadName ? ` — ${leadName}` : ''}</DialogTitle>
          <DialogDescription>
            Manual fallback. Once the Android app is rolled out, calls will be auto-logged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="ct-mobile">Mobile Number *</Label>
            <Input
              id="ct-mobile"
              value={form.mobileNumber}
              onChange={(e) => setForm((f) => ({ ...f, mobileNumber: e.target.value }))}
              placeholder="9876543210"
              data-testid="logcall-mobile-input"
            />
          </div>
          <div>
            <Label>Call Status *</Label>
            <Select value={form.callStatus} onValueChange={(v) => setForm((f) => ({ ...f, callStatus: v }))}>
              <SelectTrigger data-testid="logcall-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(STATUS_CONFIG).map((s) => (
                  <SelectItem key={s} value={s} data-testid={`logcall-status-option-${s}`}>
                    {STATUS_CONFIG[s].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="ct-duration">Duration (seconds)</Label>
            <Input
              id="ct-duration"
              type="number"
              min="0"
              value={form.durationSeconds}
              onChange={(e) => setForm((f) => ({ ...f, durationSeconds: e.target.value }))}
              placeholder="0 if not connected"
              data-testid="logcall-duration-input"
            />
            <p className="text-xs text-muted-foreground mt-1">For CONNECTED calls only. Other statuses can leave 0.</p>
          </div>
          <div>
            <Label htmlFor="ct-notes">Notes (optional)</Label>
            <Textarea
              id="ct-notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
              data-testid="logcall-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="logcall-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="logcall-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Log Call
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ----------------------------- Main Page -----------------------------
const CallActivityPage = () => {
  const { api, user } = useAuth();
  const role = user?.role;
  const isManager = ['Super Admin', 'CEO', 'Manager'].includes(role);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeTab, setActiveTab] = useState(isManager ? 'team' : 'me');

  // Data
  const [myStats, setMyStats] = useState(null);
  const [teamOverview, setTeamOverview] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  // Log call dialog
  const [logOpen, setLogOpen] = useState(false);

  const buildQS = useCallback(() => {
    const p = new URLSearchParams();
    if (dateFrom) p.set('dateFrom', dateFrom);
    if (dateTo) p.set('dateTo', dateTo);
    return p.toString() ? `?${p.toString()}` : '';
  }, [dateFrom, dateTo]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQS();
      const [meRes, listRes] = await Promise.all([
        api.get(`/call-tracking/executive/analytics${qs}`),
        api.get(`/call-tracking?limit=25${qs ? '&' + qs.slice(1) : ''}`),
      ]);
      setMyStats(meRes.data);
      setRecentCalls(listRes.data.items || []);

      if (isManager) {
        const [ovRes, lbRes] = await Promise.all([
          api.get(`/call-tracking/manager/overview${qs}`),
          api.get(`/call-tracking/manager/leaderboard?metric=connectedCalls&limit=5${qs ? '&' + qs.slice(1) : ''}`),
        ]);
        setTeamOverview(ovRes.data);
        setLeaderboard(lbRes.data);
      }
    } catch (err) {
      console.error('Call analytics load error:', err);
    } finally {
      setLoading(false);
    }
  }, [api, buildQS, isManager]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const applyPreset = (preset) => {
    const now = new Date();
    const today = now.toISOString().slice(0, 10);
    if (preset === 'today') { setDateFrom(today); setDateTo(today); return; }
    if (preset === 'yesterday') {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      const ys = y.toISOString().slice(0, 10);
      setDateFrom(ys); setDateTo(ys); return;
    }
    if (preset === 'week') {
      const w = new Date(now); w.setDate(w.getDate() - 6);
      setDateFrom(w.toISOString().slice(0, 10)); setDateTo(today); return;
    }
    if (preset === 'month') {
      const m = new Date(now.getFullYear(), now.getMonth(), 1);
      setDateFrom(m.toISOString().slice(0, 10)); setDateTo(today); return;
    }
    setDateFrom(''); setDateTo('');
  };

  const exportData = async (format) => {
    try {
      const qs = buildQS();
      const url = `/call-tracking/export?format=${format}${qs ? '&' + qs.slice(1) : ''}`;
      const resp = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([resp.data], {
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const dl = document.createElement('a');
      dl.href = URL.createObjectURL(blob);
      dl.download = `call_tracking_${new Date().toISOString().slice(0,10)}.${format}`;
      dl.click();
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (_e) {
      toast.error('Export failed');
    }
  };

  const heroStats = isManager && teamOverview ? teamOverview.totals : myStats;

  return (
    <div className="space-y-6" data-testid="call-activity-page">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Call Activity</h1>
          <p className="text-muted-foreground">
            {isManager
              ? "Team-wide calling KPIs, leaderboards, and per-executive deep-dive."
              : "Your calling activity at a glance."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={loadAll} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={() => setLogOpen(true)} data-testid="log-call-open-btn">
            <Plus className="h-4 w-4 mr-2" /> Log Call
          </Button>
          {isManager && (
            <>
              <Button variant="outline" onClick={() => exportData('csv')} data-testid="export-csv-btn">
                <Download className="h-4 w-4 mr-2" /> CSV
              </Button>
              <Button variant="outline" onClick={() => exportData('xlsx')} data-testid="export-xlsx-btn">
                <Download className="h-4 w-4 mr-2" /> Excel
              </Button>
            </>
          )}
        </div>
      </div>

      {/* DATE FILTER BAR */}
      <Card data-testid="filter-card">
        <CardContent className="pt-5">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="date-from-input" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="date-to-input" />
            </div>
            <div className="flex gap-1 ml-auto flex-wrap">
              <Button size="sm" variant="outline" onClick={() => applyPreset('today')} data-testid="preset-today">Today</Button>
              <Button size="sm" variant="outline" onClick={() => applyPreset('yesterday')} data-testid="preset-yesterday">Yesterday</Button>
              <Button size="sm" variant="outline" onClick={() => applyPreset('week')} data-testid="preset-week">This Week</Button>
              <Button size="sm" variant="outline" onClick={() => applyPreset('month')} data-testid="preset-month">This Month</Button>
              <Button size="sm" variant="ghost" onClick={() => applyPreset('all')} data-testid="preset-all">All</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* HERO STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hero-stats">
        <Stat label="Total Calls" value={heroStats?.totalCalls ?? 0} icon={Phone} testId="stat-total" />
        <Stat label="Connected" value={heroStats?.connectedCalls ?? 0} tone="success" icon={PhoneCall} testId="stat-connected" />
        <Stat label="Missed" value={heroStats?.missedCalls ?? 0} tone="warn" icon={PhoneMissed} testId="stat-missed" />
        <Stat label="Rejected" value={heroStats?.rejectedCalls ?? 0} tone="danger" icon={PhoneOff} testId="stat-rejected" />
        <Stat label="Busy" value={heroStats?.busyCalls ?? 0} tone="warn" icon={Phone} testId="stat-busy" />
        <Stat label="No Answer" value={heroStats?.noAnswerCalls ?? 0} icon={PhoneOff} testId="stat-no-answer" />
        <Stat label="Total Talk Time" value={fmtDuration(heroStats?.totalTalkTimeSeconds)} tone="info" icon={Clock} testId="stat-talk-time" />
        <Stat label="Avg Duration" value={fmtDuration(heroStats?.averageCallDurationSeconds)} icon={Clock} testId="stat-avg-duration" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {isManager && <TabsTrigger value="team" data-testid="tab-team">Team Overview</TabsTrigger>}
          {isManager && <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">Leaderboard</TabsTrigger>}
          <TabsTrigger value="me" data-testid="tab-me">{isManager ? 'My Calls' : 'Recent Calls'}</TabsTrigger>
        </TabsList>

        {/* ---- TEAM OVERVIEW (managers) ---- */}
        {isManager && (
          <TabsContent value="team">
            <Card data-testid="team-overview-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Per-Executive Breakdown</CardTitle>
                <CardDescription>Sorted by total calls (highest first). Click an executive to filter.</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    <Loader2 className="inline h-4 w-4 mr-2 animate-spin" /> Loading…
                  </p>
                ) : (
                  <div className="border rounded-md overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Executive</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right text-emerald-700">Connected</TableHead>
                          <TableHead className="text-right text-amber-700">Missed</TableHead>
                          <TableHead className="text-right text-rose-700">Rejected</TableHead>
                          <TableHead className="text-right">Busy</TableHead>
                          <TableHead className="text-right">No Ans</TableHead>
                          <TableHead className="text-right">Talk Time</TableHead>
                          <TableHead className="text-right">Avg</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(teamOverview?.rows || []).map((r) => (
                          <TableRow key={r.executiveId} data-testid={`team-row-${r.executiveId}`}>
                            <TableCell className="font-medium">{r.executiveName}</TableCell>
                            <TableCell><Badge variant="outline">{r.role}</Badge></TableCell>
                            <TableCell className="text-right font-mono">{r.totalCalls}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-700">{r.connectedCalls}</TableCell>
                            <TableCell className="text-right font-mono text-amber-700">{r.missedCalls}</TableCell>
                            <TableCell className="text-right font-mono text-rose-700">{r.rejectedCalls}</TableCell>
                            <TableCell className="text-right font-mono">{r.busyCalls}</TableCell>
                            <TableCell className="text-right font-mono">{r.noAnswerCalls}</TableCell>
                            <TableCell className="text-right font-mono">{fmtDuration(r.totalTalkTimeSeconds)}</TableCell>
                            <TableCell className="text-right font-mono">{fmtDuration(r.averageCallDurationSeconds)}</TableCell>
                          </TableRow>
                        ))}
                        {(!teamOverview?.rows || teamOverview.rows.length === 0) && (
                          <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-6">No call activity yet</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ---- LEADERBOARD (managers) ---- */}
        {isManager && (
          <TabsContent value="leaderboard">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card data-testid="top-performers-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-emerald-700">
                    <Trophy className="h-5 w-5" /> Top Performers
                  </CardTitle>
                  <CardDescription>By connected calls</CardDescription>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {(leaderboard?.top || []).map((r, idx) => (
                      <li key={r.executiveId} className="flex items-center justify-between p-3 border rounded-lg bg-emerald-50/40">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-lg text-emerald-700 w-6 text-center">#{idx + 1}</span>
                          <div>
                            <p className="font-medium">{r.executiveName}</p>
                            <p className="text-xs text-muted-foreground">{r.role} · {fmtDuration(r.totalTalkTimeSeconds)} talk</p>
                          </div>
                        </div>
                        <Badge className="bg-emerald-600">{r.connectedCalls}</Badge>
                      </li>
                    ))}
                    {(!leaderboard?.top || leaderboard.top.length === 0) && (
                      <p className="text-sm text-muted-foreground text-center py-4">No data</p>
                    )}
                  </ol>
                </CardContent>
              </Card>
              <Card data-testid="bottom-performers-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-rose-700">
                    <TrendingDown className="h-5 w-5" /> Needs Attention
                  </CardTitle>
                  <CardDescription>Lowest connected-call counts</CardDescription>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {(leaderboard?.bottom || []).map((r, idx) => (
                      <li key={r.executiveId} className="flex items-center justify-between p-3 border rounded-lg bg-rose-50/40">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-lg text-rose-700 w-6 text-center">#{idx + 1}</span>
                          <div>
                            <p className="font-medium">{r.executiveName}</p>
                            <p className="text-xs text-muted-foreground">{r.role} · Total: {r.totalCalls}</p>
                          </div>
                        </div>
                        <Badge variant="outline">{r.connectedCalls}</Badge>
                      </li>
                    ))}
                    {(!leaderboard?.bottom || leaderboard.bottom.length === 0) && (
                      <p className="text-sm text-muted-foreground text-center py-4">No data</p>
                    )}
                  </ol>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {/* ---- MY / RECENT CALLS ---- */}
        <TabsContent value="me">
          <Card data-testid="recent-calls-card">
            <CardHeader>
              <CardTitle>Recent Calls</CardTitle>
              <CardDescription>
                {isManager ? 'Latest call activity across the team.' : 'Your 25 most recent calls.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Executive</TableHead>
                      <TableHead>Lead / Mobile</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead className="text-right">Duration</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentCalls.map((c) => (
                      <TableRow key={c.id} data-testid={`recent-row-${c.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(c.startTime)}</TableCell>
                        <TableCell className="text-sm">{c.executiveName || '—'}</TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{c.leadName || '—'}</div>
                          <div className="font-mono text-xs text-muted-foreground">{c.mobileNumber}</div>
                        </TableCell>
                        <TableCell><StatusBadge status={c.callStatus} /></TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{c.source}</Badge></TableCell>
                        <TableCell className="text-right font-mono">{fmtDuration(c.durationSeconds)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{c.notes || '—'}</TableCell>
                      </TableRow>
                    ))}
                    {recentCalls.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No calls logged yet</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <LogCallDialog
        open={logOpen}
        onOpenChange={setLogOpen}
        onLogged={() => loadAll()}
      />
    </div>
  );
};

export default CallActivityPage;
